import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { RootState } from '../store/store';
import { catalogApi, CatalogCategory } from '../services/catalogApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { WhatsappPhone } from '../store/services/whatsappApi';
import MainLayout from '../components/layout/MainLayout';

interface FormData {
  name: string;
  description: string;
  price: string;
  currency: string;
  retailer_id: string;
  url: string;
  is_available: boolean;
  whatsapp_phone_id: number;
  category_ids: number[];
  image_ids: number[];
}

interface CategoryFormData {
  name: string;
  description: string;
}

const CatalogItemEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const isEditMode = !!id;
  
  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    price: '',
    currency: 'KES', // Default to Kenyan Shilling
    retailer_id: '',
    url: '',
    is_available: true,
    whatsapp_phone_id: 0,
    category_ids: [],
    image_ids: []
  });
  
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [selectedImages, setSelectedImages] = useState<{ id: number, name: string, url: string }[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  
  // Category modal state
  const [categoryModalOpen, setCategoryModalOpen] = useState<boolean>(false);
  const [categoryForm, setCategoryForm] = useState<CategoryFormData>({
    name: '',
    description: ''
  });
  const [categoryFormErrors, setCategoryFormErrors] = useState<Partial<Record<keyof CategoryFormData, string>>>({});

  // Category search state
  const [categorySearchOpen, setCategorySearchOpen] = useState<boolean>(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState<string>('');
  
  // RTK Query hooks
  const { data: whatsappPhones, isLoading: phonesLoading } = catalogApi.useGetWhatsappPhonesQuery();
  const { data: categories, isLoading: categoriesLoading, refetch: refetchCategories } = catalogApi.useGetCatalogCategoriesQuery();
  const { data: mediaAssets, isLoading: mediaLoading, refetch: refetchMedia } = catalogApi.useGetMediaAssetsQuery();
  
  const { data: itemData, isLoading: itemLoading } = 
    catalogApi.useGetCatalogItemQuery(parseInt(id || '0'), { skip: !isEditMode });
  
  const [createItem, { isLoading: isCreating }] = catalogApi.useCreateCatalogItemMutation();
  const [updateItem, { isLoading: isUpdating }] = catalogApi.useUpdateCatalogItemMutation();
  const [uploadMedia] = catalogApi.useUploadMediaMutation();
  const [createCategory, { isLoading: isCreatingCategory }] = catalogApi.useCreateCatalogCategoryMutation();
  
  // Set default WhatsApp phone when data loads
  useEffect(() => {
    if (whatsappPhones && whatsappPhones.length > 0 && formData.whatsapp_phone_id === 0) {
      setFormData(prev => ({ ...prev, whatsapp_phone_id: whatsappPhones[0].id }));
    }
  }, [whatsappPhones, formData.whatsapp_phone_id]);
  
  // Load catalog item data for edit mode
  useEffect(() => {
    if (isEditMode && itemData && !itemLoading) {
      // Set form data from catalog item
      setFormData({
        name: itemData.name,
        description: itemData.description || '',
        price: itemData.price.toString(),
        currency: itemData.currency,
        retailer_id: itemData.retailer_id || '',
        url: itemData.url || '',
        is_available: itemData.is_available,
        whatsapp_phone_id: itemData.whatsapp_phone_id,
        category_ids: itemData.categories.map(cat => cat.id),
        image_ids: itemData.images.map(img => img.id),
      });
      
      // Set selected images
      setSelectedImages(
        itemData.images.map(img => ({
          id: img.id,
          name: img.file_name,
          url: img.original_url || ''
        }))
      );
    }
  }, [isEditMode, itemData, itemLoading]);
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when field is edited
    if (formErrors[name as keyof FormData]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };
  

  
  // Handle category form input change
  const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCategoryForm(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (categoryFormErrors[name as keyof CategoryFormData]) {
      setCategoryFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };
  
  // Handle category form submission
  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const errors: Partial<Record<keyof CategoryFormData, string>> = {};

    if (!categoryForm.name) {
      errors.name = 'Category name is required';
    }

    if (Object.keys(errors).length > 0) {
      setCategoryFormErrors(errors);
      return;
    }

    try {
      // Create category
      const newCategory = await createCategory({
        name: categoryForm.name,
        description: categoryForm.description,
      }).unwrap();

      // Add new category to selected categories
      if (newCategory && newCategory.id) {
        setFormData(prev => ({
          ...prev,
          category_ids: [...prev.category_ids, newCategory.id]
        }));
      }

      // Reset form and close modal
      setCategoryForm({ name: '', description: '' });
      setCategoryModalOpen(false);

      // Refetch categories
      refetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  // Handle category selection for searchable dropdown
  const handleCategoryToggle = (categoryId: number) => {
    const isSelected = formData.category_ids.includes(categoryId);
    if (isSelected) {
      // Remove category
      setFormData(prev => ({
        ...prev,
        category_ids: prev.category_ids.filter(id => id !== categoryId)
      }));
    } else {
      // Add category
      setFormData(prev => ({
        ...prev,
        category_ids: [...prev.category_ids, categoryId]
      }));
    }
  };

  // Filter categories based on search term
  const filteredCategories = categories?.filter(category =>
    category.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
  ) || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categorySearchOpen && !(event.target as Element).closest('.category-dropdown')) {
        setCategorySearchOpen(false);
        setCategorySearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categorySearchOpen]);
  
  // Handle image selection
  const handleImageSelection = (imageId: number) => {
    const isSelected = formData.image_ids.includes(imageId);
    
    if (isSelected) {
      // Remove image
      setFormData(prev => ({
        ...prev,
        image_ids: prev.image_ids.filter(id => id !== imageId)
      }));
      
      setSelectedImages(prev => prev.filter(img => img.id !== imageId));
    } else {
      // Add image (if we have less than 10 images)
      if (formData.image_ids.length < 10) {
        setFormData(prev => ({
          ...prev,
          image_ids: [...prev.image_ids, imageId]
        }));
        
        const selectedImage = mediaAssets?.find(img => img.id === imageId);
        if (selectedImage) {
          setSelectedImages(prev => [
            ...prev,
            {
              id: selectedImage.id,
              name: selectedImage.file_name,
              url: selectedImage.original_url || ''
            }
          ]);
        }
      }
    }
  };
  
  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  // Common file handling logic
  const handleFiles = (files: File[]) => {
    // Filter for image files only
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      alert('Only image files are allowed. Non-image files have been ignored.');
    }

    if (imageFiles.length === 0) return;

    // Check if adding these files would exceed the 10 image limit
    const totalImages = formData.image_ids.length + imageFiles.length;
    if (totalImages > 10) {
      alert('You can only upload a maximum of 10 images per catalog item');
      return;
    }

    // Add files to upload list
    setUploadedFiles(prev => [...prev, ...imageFiles]);
  };
  
  // Upload files
  const uploadFiles = async () => {
    if (uploadedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload each file
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_type', 'image'); // Required by backend

        // Upload file
        const result = await uploadMedia(formData).unwrap();
        
        // Add uploaded image to selected images
        if (result && result.id) {
          handleImageSelection(result.id);
        }
        
        // Update progress
        setUploadProgress(Math.round(((i + 1) / uploadedFiles.length) * 100));
      }
      
      // Clear uploaded files
      setUploadedFiles([]);
      
      // Refetch media assets
      refetchMedia();
      
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setIsUploading(false);
    }
  };
  
  // Validate form
  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof FormData, string>> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.length > 150) {
      errors.name = 'Name must be 150 characters or less';
    }
    
    if (formData.description && formData.description.length > 1000) {
      errors.description = 'Description must be 1000 characters or less';
    }
    
    if (!formData.price.trim()) {
      errors.price = 'Price is required';
    } else if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) < 0) {
      errors.price = 'Price must be a valid number';
    }
    
    if (!formData.currency.trim()) {
      errors.currency = 'Currency is required';
    }
    
    if (!formData.whatsapp_phone_id) {
      errors.whatsapp_phone_id = 'WhatsApp phone is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return; // Stop if validation fails
    }
    
    try {
      // Prepare data for API
      const catalogItemData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        currency: formData.currency,
        retailer_id: formData.retailer_id || undefined,
        url: formData.url || undefined,
        is_available: formData.is_available,
        whatsapp_phone_id: formData.whatsapp_phone_id,
        category_ids: formData.category_ids.length > 0 ? formData.category_ids : undefined,
        image_ids: formData.image_ids.length > 0 ? formData.image_ids : undefined,
      };
      
      if (isEditMode) {
        // Update existing item
        await updateItem({
          id: parseInt(id || '0'),
          ...catalogItemData
        }).unwrap();
      } else {
        // Create new item
        await createItem(catalogItemData).unwrap();
      }
      
      // Navigate back to catalog management
      navigate('/catalog');
    } catch (error) {
      console.error('Error saving catalog item:', error);
    }
  };
  
  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-yellow-700">
            You need to be logged in to manage catalog items.
          </p>
        </div>
      </div>
    );
  }
  
  if (isEditMode && itemLoading) {
    return <LoadingSpinner />;
  }
  
  return (
    <MainLayout>
      <div className="container mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">
          {isEditMode ? 'Edit Catalog Item' : 'Add New Catalog Item'}
        </h1>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        {/* Basic Information */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
          
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green`}
              placeholder="Product name (max 150 characters)"
              maxLength={150}
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-3 py-2 border ${formErrors.description ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green`}
              placeholder="Product description (max 1000 characters)"
              maxLength={1000}
            />
            {formErrors.description && (
              <p className="mt-1 text-sm text-red-500">{formErrors.description}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              {formData.description.length}/1000 characters
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                Price <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className={`w-full px-3 py-2 border ${formErrors.price ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green`}
                placeholder="0.00"
              />
              {formErrors.price && (
                <p className="mt-1 text-sm text-red-500">{formErrors.price}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                Currency <span className="text-red-500">*</span>
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${formErrors.currency ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green`}
              >
                <option value="KES">KES - Kenyan Shilling</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="TZS">TZS - Tanzanian Shilling</option>
                <option value="UGX">UGX - Ugandan Shilling</option>
                <option value="RWF">RWF - Rwandan Franc</option>
              </select>
              {formErrors.currency && (
                <p className="mt-1 text-sm text-red-500">{formErrors.currency}</p>
              )}
            </div>
          </div>
          
          <div className="mb-4">
            <label htmlFor="retailer_id" className="block text-sm font-medium text-gray-700 mb-1">
              Retailer ID (Optional)
            </label>
            <input
              type="text"
              id="retailer_id"
              name="retailer_id"
              value={formData.retailer_id}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
              placeholder="Your internal product ID"
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
              Product URL (Optional)
            </label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
              placeholder="https://example.com/product"
            />
          </div>
          
          <div className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_available"
                name="is_available"
                checked={formData.is_available}
                onChange={handleInputChange}
                className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
              />
              <label htmlFor="is_available" className="ml-2 block text-sm text-gray-900">
                Item is in stock
              </label>
            </div>
          </div>
        </div>
        
        {/* WhatsApp Phone */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">WhatsApp Phone</h2>
          
          <div className="mb-4">
            <label htmlFor="whatsapp_phone_id" className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp Phone <span className="text-red-500">*</span>
            </label>
            <select
              id="whatsapp_phone_id"
              name="whatsapp_phone_id"
              value={formData.whatsapp_phone_id || ''}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border ${formErrors.whatsapp_phone_id ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green`}
              disabled={phonesLoading || isEditMode}
            >
              <option value="">Select WhatsApp Phone</option>
              {phonesLoading ? (
                <option>Loading phones...</option>
              ) : whatsappPhones && whatsappPhones.length > 0 ? (
                whatsappPhones.map((phone: WhatsappPhone) => (
                  <option key={phone.id} value={phone.id}>
                    {phone.display_name} ({phone.phone_number})
                  </option>
                ))
              ) : (
                <option>No WhatsApp phones available</option>
              )}
            </select>
            {formErrors.whatsapp_phone_id && (
              <p className="mt-1 text-sm text-red-500">{formErrors.whatsapp_phone_id}</p>
            )}
            {isEditMode && (
              <p className="mt-1 text-sm text-gray-500">
                WhatsApp phone cannot be changed after creation
              </p>
            )}
          </div>
        </div>
        
        {/* Categories */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Categories (Optional)</h2>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Select Categories
              </label>
              <button
                type="button"
                onClick={() => setCategoryModalOpen(true)}
                className="text-sm text-whatsapp-green hover:text-whatsapp-dark-green flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add New Category
              </button>
            </div>

            {/* Selected categories display */}
            {formData.category_ids.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {formData.category_ids.map(categoryId => {
                  const category = categories?.find(cat => cat.id === categoryId);
                  return category ? (
                    <span
                      key={categoryId}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-whatsapp-green text-white"
                    >
                      {category.name}
                      <button
                        type="button"
                        onClick={() => handleCategoryToggle(categoryId)}
                        className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-whatsapp-dark-green"
                      >
                        ×
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {/* Searchable dropdown */}
            <div className="relative category-dropdown">
              <div
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green cursor-pointer bg-white"
                onClick={() => setCategorySearchOpen(!categorySearchOpen)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">
                    {formData.category_ids.length === 0
                      ? 'Select categories...'
                      : `${formData.category_ids.length} category(ies) selected`
                    }
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${categorySearchOpen ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {categorySearchOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {/* Search input */}
                  <div className="p-2 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={categorySearchTerm}
                      onChange={(e) => setCategorySearchTerm(e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-whatsapp-green"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Category list */}
                  <div className="py-1">
                    {categoriesLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Loading categories...</div>
                    ) : filteredCategories.length > 0 ? (
                      filteredCategories.map((category: CatalogCategory) => {
                        const isSelected = formData.category_ids.includes(category.id);
                        return (
                          <div
                            key={category.id}
                            className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                              isSelected ? 'bg-whatsapp-green bg-opacity-10' : ''
                            }`}
                            onClick={() => handleCategoryToggle(category.id)}
                          >
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}} // Handled by onClick
                                className="mr-2 h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">{category.name}</span>
                              {category.description && (
                                <span className="text-xs text-gray-500 ml-2">({category.description})</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        {categorySearchTerm ? 'No categories found' : 'No categories available'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Images */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Images</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Selected Images ({selectedImages.length}/10)
            </label>
            
            {selectedImages.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-2">
                {selectedImages.map(image => (
                  <div key={image.id} className="relative">
                    <img
                      src={image.url}
                      alt={image.name}
                      className="h-24 w-24 object-cover rounded border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => handleImageSelection(image.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-6 h-6 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No images selected</p>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload New Images
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragOver
                  ? 'border-whatsapp-green bg-green-50'
                  : 'border-gray-300 hover:border-gray-400'
              } ${isUploading || selectedImages.length >= 10 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !isUploading && selectedImages.length < 10 && document.getElementById('file-input')?.click()}
            >
              <div className="flex flex-col items-center">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p className="text-gray-600 mb-2">
                  {isDragOver ? 'Drop images here' : 'Drag and drop images here, or click to select'}
                </p>
                <p className="text-sm text-gray-500">
                  Accepted formats: JPG, JPEG, PNG. Max size: 5MB. Max 10 images.
                </p>
              </div>
            </div>
            <input
              id="file-input"
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              multiple
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading || selectedImages.length >= 10}
            />
            
            {uploadedFiles.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700">
                  {uploadedFiles.length} file(s) ready to upload
                </p>
                <button
                  type="button"
                  onClick={uploadFiles}
                  disabled={isUploading}
                  className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {isUploading ? 'Uploading...' : 'Upload Files'}
                </button>
                
                {isUploading && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {uploadProgress}% complete
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select from Media Library
            </label>
            
            {mediaLoading ? (
              <p className="text-gray-500">Loading media assets...</p>
            ) : mediaAssets && mediaAssets.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-2">
                {mediaAssets
                  .filter(asset => asset.file_type === 'image')
                  .map(asset => {
                    const isSelected = formData.image_ids.includes(asset.id);
                    return (
                      <div
                        key={asset.id}
                        onClick={() => handleImageSelection(asset.id)}
                        className={`cursor-pointer border rounded p-1 ${
                          isSelected ? 'border-whatsapp-green bg-green-50' : 'border-gray-300'
                        }`}
                      >
                        <img
                          src={asset.original_url || ''}
                          alt={asset.file_name}
                          className="h-20 w-20 object-cover mx-auto"
                        />
                        <div className="mt-1 text-xs text-center truncate">
                          {asset.file_name}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-gray-500">No media assets available</p>
            )}
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/catalog')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating || isUpdating || isUploading}
            className="px-4 py-2 bg-whatsapp-green text-white rounded hover:bg-whatsapp-dark-green disabled:opacity-50"
          >
            {isCreating || isUpdating ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditMode ? 'Update Item' : 'Create Item'
            )}
          </button>
        </div>
      </form>
      
      {/* Category Modal */}
      {categoryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Add New Category</h2>
              <button
                type="button"
                onClick={() => setCategoryModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleCategorySubmit}>
              <div className="mb-4">
                <label htmlFor="category-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="category-name"
                  name="name"
                  value={categoryForm.name}
                  onChange={handleCategoryInputChange}
                  className={`w-full px-3 py-2 border ${categoryFormErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green`}
                  placeholder="e.g., Electronics, Clothing, etc."
                />
                {categoryFormErrors.name && (
                  <p className="mt-1 text-sm text-red-500">{categoryFormErrors.name}</p>
                )}
              </div>
              
              <div className="mb-4">
                <label htmlFor="category-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  id="category-description"
                  name="description"
                  value={categoryForm.description}
                  onChange={handleCategoryInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                  placeholder="Brief description of this category"
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setCategoryModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-dark-green flex items-center"
                  disabled={isCreatingCategory}
                >
                  {isCreatingCategory ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    'Create Category'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </MainLayout>
  );
};

export default CatalogItemEditor;
